import React from 'react';
import ReactTable, { RowInfo } from 'react-table';
import moment from 'moment';
import { Box } from 'bloomer/lib/elements/Box';
import Animal from '../Entities/Animal';
import styled from 'styled-components';
import Conditional from './Conditional';
import { Icon } from 'bloomer/lib/elements/Icon';
import Bold from '../elements/Bold';
import AssessmentCounter from '../elements/AssessmentCountdown';
import Spacer from '../elements/Spacer';

interface StateTypes {
  time: moment.Moment;
}

interface PropTypes {
  animals: Animal[];
  loading: boolean;
  enrichmentData: Enrichment[];
  handleRowClick?: (a: Animal) => void;
}

const BioSecurityIcon = styled.img<any>`
  max-width: 17px;
`;

const NewLine = styled.span<any>`
  display: block;
  ${({ size }: { size?: string }) => (size === 'small' ? 'font-size: 10px;' : '')}
`;

const WrappableP = styled.p<any>`
  white-space: normal;
`;

const StrikeThrough = styled.span`
  text-decoration: line-through;
`;
export default class EnrichmentStatusTable extends React.Component<PropTypes, StateTypes> {
  timerID: number | null = null;
  state = {
    time: moment(),
  };

  updateTime = () => {
    this.setState({
      time: moment(),
    });
  };

  getEnrichmentTime = (aNumber: number) => {
    const { enrichmentData } = this.props;
    return enrichmentData
      .filter(data => data.aNumber === aNumber)
      .reduce((acc, { minutesOfEnrichment }) => acc + minutesOfEnrichment, 0);
  };

  getNumberOfEnrichments = (aNumber: number) => {
    const { enrichmentData } = this.props;
    return enrichmentData.filter(data => data.aNumber === aNumber).length;
  };

  handleRowClick = (state: any, rowInfo: RowInfo | undefined) => ({
    onClick: () => {
      const { handleRowClick } = this.props;
      if (handleRowClick && rowInfo) {
        handleRowClick(rowInfo.original);
      }
    },
  });

  componentDidMount() {
    this.updateTime();
    this.timerID = setInterval(() => {
      this.updateTime();
    }, 60000);
  }

  getLastTimeOutObject = (animal: Animal) => {
    const { time } = this.state;
    if (animal.isCat() && animal.lastCountableEnrichment) {
      return {
        animal,
        time: moment(animal.lastCountableEnrichment.timeReturned).format('h:mmA D MMM'),
        type: `(${animal.lastCountableEnrichment.enrichmentType})`,
        needsOut: time.diff(animal.lastCountableEnrichment.timeReturned, 'hours', true) > 5,
      };
    }

    if (animal.isDog() && animal.previousEnrichment) {
      return {
        animal,
        time: moment(animal.previousEnrichment.timeReturned).format('h:mmA D MMM'),
        type: `(${animal.previousEnrichment.enrichmentType})`,
        needsOut: time.diff(animal.previousEnrichment.timeReturned, 'hours', true) > 5,
      };
    }

    return { animal, time: '', type: '', needsOut: false };
  };

  render() {
    const { animals, loading } = this.props;
    return (
      <Box>
        <ReactTable
          data={animals}
          loading={loading}
          style={{ textAlign: 'center' }}
          // tslint:disable-next-line:jsx-no-lambda
          getTrProps={(state: any, rowInfo: RowInfo | undefined) => {
            if (!rowInfo) return;
            const aNumber = rowInfo.original.aNumber;
            return {
              id: `summary-row-${aNumber}`,
            };
          }}
          columns={[
            {
              Header: (
                <p>
                  <NewLine>Bio</NewLine>
                  <NewLine>Security</NewLine>
                  <NewLine>Level</NewLine>
                </p>
              ),
              id: 'bio-security',
              maxWidth: 75,
              accessor: animal => (
                <BioSecurityIcon
                  key={`animal-bio-security-${animal.locationEntity.bioSecurity}-${animal.aNumber}`}
                  src={animal.locationEntity.getIcon()}
                  alt={animal.locationEntity.getHelpText()}
                  title={animal.locationEntity.getHelpText()}
                />
              ),
            },
            {
              Header: (
                <p>
                  <NewLine>Location</NewLine>
                  <NewLine>Name</NewLine>
                </p>
              ),
              id: 'location',
              accessor: animal =>
                animal
                  .getLocationName()
                  .split(' ')
                  .map(word => <NewLine key={`${animal.aNumber}-${word}-location`}>{word}</NewLine>),
              minWidth: 60,
            },
            {
              Header: (
                <p>
                  <NewLine>Status</NewLine>
                  <NewLine size="small">(Red/Green,</NewLine>
                  <NewLine size="small">Tag,</NewLine>
                  <NewLine size="small">Longstay,</NewLine>
                  <NewLine size="small">Assessment)</NewLine>
                </p>
              ),
              id: 'special-need-symbols',
              accessor: animal => (
                <p>
                  <Conditional check={animal.isCat() && animal.hasMedicalTag()}>
                    <NewLine>
                      <Icon
                        style={{
                          color: animal.medicalTag ? animal.medicalTag : '',
                        }}
                        title="Long Stay"
                        className="fa fa-circle"
                      />
                    </NewLine>
                  </Conditional>
                  <Conditional check={animal.isLongStay && animal.isDog()}>
                    <NewLine>
                      <Icon title="Long Stay" className="fa fa-star is-yellow" />
                    </NewLine>
                  </Conditional>
                  <Conditional check={animal.isDog()}>
                    <AssessmentCounter assessmentStatus={animal.assessmentStatus} dueDate={animal.nextAssessmentDue} />
                  </Conditional>
                </p>
              ),
              maxWidth: 85,
            },

            { Header: 'Name', accessor: 'name' },
            {
              Header: (
                <p>
                  <NewLine>Colour</NewLine>
                  <NewLine>Code</NewLine>
                </p>
              ),
              id: 'animal-color-column',
              accessor: animal => {
                const hasSpecialNeedUnderline = animal.specialNeeds.some(need =>
                  need.specialBehaviours.includes('underline'),
                );
                if (!hasSpecialNeedUnderline) {
                  return {
                    color: animal.colorCode,
                    underline: null,
                    acronym: null,
                  };
                }
                const need = animal.specialNeeds
                  .filter(need => need.specialBehaviours.includes('underline'))
                  .sort((a, b) => a.priority - b.priority)[0];
                return {
                  underline: need.color,
                  color: animal.colorCode,
                  acronym: need.acronym,
                };
              },
              Cell: ({ value }) => {
                const { underline, color, acronym } = value;
                const ColorSpan = styled.span`
                  border-bottom: 4px solid ${underline || color};
                `;
                return (
                  <>
                    <Conditional check={Boolean(underline)}>
                      <StrikeThrough>{color}</StrikeThrough>
                    </Conditional>
                    <Spacer>
                      <ColorSpan>{acronym || color}</ColorSpan>
                    </Spacer>
                  </>
                );
              },
            },
            {
              Header: (
                <p>
                  <NewLine>Training</NewLine>
                  <NewLine>Plan</NewLine>
                </p>
              ),
              accessor: 'trainingPlan',
              minWidth: 50,
            },
            {
              Header: (
                <p>
                  <NewLine>Total</NewLine>
                  <NewLine>Time</NewLine>
                  <NewLine>(Min)</NewLine>
                </p>
              ),
              id: 'totalTime',
              accessor: animal => this.getEnrichmentTime(animal.aNumber),
            },
            {
              Header: (
                <p>
                  <NewLine>Enrich</NewLine>
                  <NewLine>Number</NewLine>
                </p>
              ),
              id: 'numberOfEnrichments',
              accessor: animal => this.getNumberOfEnrichments(animal.aNumber),
              minWidth: 50,
            },
            {
              // TODO: Add background color when time elapsed
              Header: (
                <p>
                  <NewLine>Last</NewLine>
                  <NewLine>Out</NewLine>
                </p>
              ),
              id: 'lastTimeOut',
              accessor: animal => {
                return this.getLastTimeOutObject(animal);
              },
              Cell: ({ value }: { value: any }) => {
                const { time, type, needsOut } = value;
                const animal = value.animal as Animal;
                if (!animal.signOutData) {
                  return (
                    <p className={needsOut ? 'needsOut' : ''}>
                      <NewLine>{time}</NewLine>
                      <NewLine>{type}</NewLine>
                    </p>
                  );
                }

                return (
                  <WrappableP>
                    Out for a <Bold>{animal.signOutData.enrichmentType}</Bold> with{' '}
                    <Bold>{animal.signOutData.walkerName}</Bold> since{' '}
                    <Bold>{moment(animal.signOutData.timeTakenOut).format('hh:mmA')}</Bold>
                  </WrappableP>
                );
              },
            },
          ]}
          defaultPageSize={100}
          pageSize={animals.length}
          minRows={0}
          showPagination={false}
          className="-striped -highlight"
          resizable={false}
          sortable={false}
          getTdProps={this.handleRowClick}
        />
      </Box>
    );
  }

  componentWillUnmount() {
    if (this.timerID) {
      clearInterval(this.timerID);
    }
  }
}
